import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import EditorLayout from './Layout/EditorLayout';
import EventCard from './EventCard';
import EventPreview from './EventPreview';
import { 
    GroupIcon as Users, 
    MailIcon as Mail, 
    SettingsIcon as Settings,
    EditIcon as Edit,
    CheckCircleIcon as CheckCircle,
    CloseIcon as X,
    RefreshIcon as RefreshCw,
    SaveIcon as Save,
    DeleteIcon as Trash2
} from './Icons';
import InlineFieldEditor from './InlineFieldEditor';

const EventEditor = ({ eventId, onClose, allUsers }) => {
    const { user, API_URL } = useContext(AuthContext);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('card'); // 'card', 'page', 'admin'
    const [registrations, setRegistrations] = useState([]);
    const [loadingRegistrations, setLoadingRegistrations] = useState(false);

    useEffect(() => {
        fetchEvent();
    }, [eventId]);

    const canEdit = event && (
        user.role === 'admin' || 
        (event.createdBy?._id || event.createdBy) === user._id || 
        event.editableBy?.some(u => (u._id || u) === user._id)
    );

    const fetchEvent = async () => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` }
            };
            const response = await axios.get(`${API_URL}/events/${eventId}`, config);
            setEvent(response.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching event:", err);
            setLoading(false);
        }
    };

    const fetchRegistrations = async () => {
        setLoadingRegistrations(true);
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` }
            };
            const response = await axios.get(`${API_URL}/events/${eventId}/registrations`, config);
            setRegistrations(response.data);
        } catch (err) {
            console.error("Error fetching registrations:", err);
        } finally {
            setLoadingRegistrations(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'admin') {
            fetchRegistrations();
        }
    }, [activeTab]);

    const handleUpdateField = async (field, value) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` }
            };
            const updatedEvent = { ...event, [field]: value };
            await axios.put(`${API_URL}/events/${eventId}`, updatedEvent, config);
            setEvent(updatedEvent);
        } catch (err) {
            console.error("Error updating event field:", err);
            alert("Fehler beim Aktualisieren.");
        }
    };

    const handleApproveRegistration = async (registrationId) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${user.token}` }
            };
            await axios.put(`${API_URL}/events/registrations/${registrationId}/approve`, {}, config);
            fetchRegistrations();
            alert("Anmeldung wurde bestätigt.");
        } catch (err) {
            console.error("Error approving registration:", err);
            alert("Fehler beim Bestätigen.");
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <RefreshCw className="animate-spin text-primary" size={48} />
        </div>
    );

    if (!event) return <div>Event nicht gefunden.</div>;

    return (
        <EditorLayout
            title="Event Editor"
            subtitle={event.title}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
                { id: "card", label: "Karte" },
                { id: "page", label: "Seite" },
                { id: "admin", label: "Verwaltung" }
            ]}
            actions={
                <button onClick={onClose} className="md3-btn-text">Schließen</button>
            }
        >
            <div className="max-w-4xl mx-auto">
                {activeTab === 'card' && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="md3-card-outlined p-8 !bg-surface-containerLow flex flex-col items-center">
                            <h3 className="text-title-large font-bold mb-6 self-start">Vorschau der Eventkarte</h3>
                            <div className="w-full max-w-sm">
                                <EventCard event={event} isEditable={canEdit} onUpdate={handleUpdateField} />
                            </div>
                            {canEdit && (
                                <p className="mt-6 text-body-medium text-surface-onVariant italic">
                                    Klicke auf die Stift-Icons in der Karte, um die Inhalte direkt zu bearbeiten.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'page' && (
                    <div className="animate-fade-in pb-20">
                         <div className="flex justify-between items-center mb-6">
                            <h3 className="text-title-large font-bold">Event-Seite bearbeiten</h3>
                            <p className="text-body-medium text-surface-onVariant">Änderungen werden sofort gespeichert.</p>
                         </div>
                         <div className="md3-card-outlined !bg-surface overflow-hidden rounded-3xl">
                            <EventPreview 
                                event={event} 
                                isEditable={canEdit} 
                                onUpdate={handleUpdateField} 
                            />
                         </div>
                    </div>
                )}

                {activeTab === 'admin' && (
                    <div className="space-y-8 animate-fade-in">
                        <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 flex items-center gap-2">
                                <Users size={24} /> Anmeldungen
                            </h3>
                            
                            {loadingRegistrations ? (
                                <div className="flex justify-center py-12"><RefreshCw className="animate-spin" /></div>
                            ) : registrations.length === 0 ? (
                                <p className="text-center py-12 text-surface-onVariant italic">Keine Anmeldungen vorhanden.</p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-outlineVariant">
                                    <table className="min-w-full divide-y divide-outlineVariant">
                                        <thead className="bg-surface-containerHigh">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-bold uppercase">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold uppercase">Status</th>
                                                <th className="px-6 py-3 text-right text-xs font-bold uppercase">Aktionen</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-outlineVariant">
                                            {registrations.map(reg => (
                                                <tr key={reg._id}>
                                                    <td className="px-6 py-4">{reg.user ? (reg.user.userData?.scoutName || reg.user.email) : reg.guestName}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${reg.status === 'confirmed' ? 'bg-primary/20 text-primary' : 'bg-tertiary/20 text-tertiary'}`}>
                                                            {reg.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        {reg.status === 'pending' && (
                                                            <button 
                                                                onClick={() => handleApproveRegistration(reg._id)}
                                                                className={`md3-btn-tonal text-xs ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                disabled={!canEdit}
                                                            >
                                                                Freigeben
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                         <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 flex items-center gap-2">
                                <Settings size={24} /> Einstellungen
                            </h3>
                            <div className={`space-y-4 ${!canEdit ? 'opacity-60 pointer-events-none' : ''}`}>
                                <div className="flex items-center justify-between p-4 bg-surface-container rounded-xl">
                                    <div>
                                        <p className="font-bold">Anmeldung aktiviert</p>
                                        <p className="text-sm text-surface-onVariant">Können sich Nutzer für dieses Event anmelden?</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={event.isRegistrationEnabled} 
                                            onChange={(e) => handleUpdateField('isRegistrationEnabled', e.target.checked)}
                                            className="sr-only peer" 
                                            disabled={!canEdit}
                                        />
                                        <div className="w-11 h-6 bg-outlineVariant rounded-full peer peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {event.isRegistrationEnabled && (
                                    <div className="pl-6 space-y-4 border-l-2 border-primary/20 ml-2">
                                        <div className="flex items-center justify-between p-4 bg-surface-container/50 rounded-xl">
                                            <div>
                                                <p className="font-bold">Manuelle Freigabe</p>
                                                <p className="text-sm text-surface-onVariant">Müssen Anmeldungen erst von dir bestätigt werden?</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    checked={event.requiresApproval} 
                                                    onChange={(e) => handleUpdateField('requiresApproval', e.target.checked)}
                                                    className="sr-only peer" 
                                                    disabled={!canEdit}
                                                />
                                                <div className="w-11 h-6 bg-outlineVariant rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between p-4 bg-surface-container/50 rounded-xl">
                                            <div>
                                                <p className="font-bold">Gesundheitsdaten</p>
                                                <p className="text-sm text-surface-onVariant">Sollen Allergien und Medikamente abgefragt werden?</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    checked={event.askHealthQuestions} 
                                                    onChange={(e) => handleUpdateField('askHealthQuestions', e.target.checked)}
                                                    className="sr-only peer" 
                                                    disabled={!canEdit}
                                                />
                                                <div className="w-11 h-6 bg-outlineVariant rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center justify-between p-4 bg-surface-container rounded-xl">
                                    <div>
                                        <p className="font-bold">Einverständniserklärung</p>
                                        <p className="text-sm text-surface-onVariant">Müssen Nutzer eine Erklärung akzeptieren?</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            checked={event.requiresConsent} 
                                            onChange={(e) => handleUpdateField('requiresConsent', e.target.checked)}
                                            className="sr-only peer" 
                                            disabled={!canEdit}
                                        />
                                        <div className="w-11 h-6 bg-outlineVariant rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between p-4 bg-surface-container rounded-xl">
                                    <div>
                                        <p className="font-bold">Status</p>
                                        <p className="text-sm text-surface-onVariant">Ist das Event für alle sichtbar?</p>
                                    </div>
                                    <select 
                                        value={event.status || 'published'} 
                                        onChange={(e) => handleUpdateField('status', e.target.value)}
                                        className="md3-textfield-outlined !w-auto"
                                        disabled={!canEdit}
                                    >
                                        <option value="draft">Entwurf</option>
                                        <option value="published">Veröffentlicht</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="md3-card-outlined p-6 !bg-surface-containerLow">
                            <h3 className="text-title-large font-bold mb-6 flex items-center gap-2">
                                <Mail size={24} /> E-Mail Bestätigung
                            </h3>
                            <div className={`space-y-4 ${!canEdit ? 'opacity-60 pointer-events-none' : ''}`}>
                                <InlineFieldEditor 
                                    label="Betreff"
                                    value={event.registrationEmailTemplate?.subject || `Anmeldung bestätigt: ${event.title}`}
                                    onSave={(val) => handleUpdateField('registrationEmailTemplate', { ...event.registrationEmailTemplate, subject: val })}
                                    onCancel={() => {}}
                                />
                                <InlineFieldEditor 
                                    label="Nachricht"
                                    type="textarea"
                                    value={event.registrationEmailTemplate?.bodyTemplate || `Hallo {"{{name}}"},\n\ndeine Anmeldung für "${event.title}" wurde erfolgreich bestätigt.`}
                                    onSave={(val) => handleUpdateField('registrationEmailTemplate', { ...event.registrationEmailTemplate, bodyTemplate: val })}
                                    onCancel={() => {}}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </EditorLayout>
    );
};

export default EventEditor;
